import { forwardRef, useCallback, useEffect, useMemo, useRef } from "react"
import { isEqual } from "lodash-es"
import { makeStyles } from "tss-react/mui"
import { useField } from "formik"
import { Alert, Divider, Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { SnackbarContent, SnackbarKey, SnackbarMessage, useSnackbar } from "notistack"

import { CreateOrderDialogType, ExtraServiceDetails, ProductWithQuantity } from "../../../createOrderTypes"
import { ExtraServiceActionType } from "../../../../../../modules/merchant/admin/extraServices/extraServicesTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { DeliveryFulfillmentType, DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { StandardButton } from "../../../../buttons/StandardButton"
import { useCreateOrderDialogType } from "../../../hooks/useCreateOrderDialogType"
import { calculateServicePrice, isServiceAdded } from "./helpers"
import { ExtraServicesAccordion } from "./ExtraServicesAccordion"

type AutoAddedServiceSnackbarProps = {
    id: SnackbarKey
    message: SnackbarMessage
    isFirst?: boolean
}

const AutoAddedServiceSnackbar = forwardRef<HTMLDivElement, AutoAddedServiceSnackbarProps>(
    ({ id, message, isFirst }, ref) => {
        const { closeSnackbar } = useSnackbar()

        return (
            <SnackbarContent ref={ref} style={{ marginTop: isFirst ? "3rem" : ".5rem" }}>
                <Alert severity="error" onClose={() => closeSnackbar(id)} sx={{ minWidth: 360, maxWidth: 403 }}>
                    {message}
                </Alert>
            </SnackbarContent>
        )
    },
)

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: theme.palette.tailwind.slate[400],
    },
    wrap: {
        pointerEvents: "auto",
    },
    extraServicesDivider: {
        marginTop: "1rem",
        marginBottom: "0.5rem",
    },
}))

type ExtraServicesDeliveryAccordionProps = Readonly<{
    services: ExtraServiceDetails[]
    dialogType: CreateOrderDialogType
    deliveryType: DeliveryFulfillmentType
    deliveryTime: string
    deliveryIndex: number
    onAutoExpand?: () => void
}>

export function ExtraServicesDeliveryAccordion({
    services,
    dialogType,
    deliveryType,
    deliveryTime,
    deliveryIndex,
    onAutoExpand,
}: ExtraServicesDeliveryAccordionProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const { l10n } = useAppLocalization()
    const { enqueueSnackbar } = useSnackbar()

    const { isRefundOrder } = useCreateOrderDialogType(dialogType)

    const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const [{ value: seatCountValue }] = useField(DynamicConfigKey.NUMBER_OF_SEATS)

    const seatCount = seatCountValue || 0

    // Stable ref for deliveriesHelpers to avoid triggering effects on every render
    const deliveriesHelpersRef = useRef(deliveriesHelpers)
    deliveriesHelpersRef.current = deliveriesHelpers

    const deliveriesRef = useRef(deliveries)
    deliveriesRef.current = deliveries

    const seatCountRef = useRef(seatCount)
    seatCountRef.current = seatCount

    const onAutoExpandRef = useRef(onAutoExpand)
    onAutoExpandRef.current = onAutoExpand

    const moneyFactoryRef = useRef(moneyFactory)
    moneyFactoryRef.current = moneyFactory

    const l10nRef = useRef(l10n)
    l10nRef.current = l10n

    const enqueueSnackbarRef = useRef(enqueueSnackbar)
    enqueueSnackbarRef.current = enqueueSnackbar

    const setDeliveries = useCallback((updater: (current: DeliveryTimesField) => DeliveryTimesField) => {
        const current = deliveriesRef.current
        const next = updater(current)
        if (!isEqual(current, next)) {
            deliveriesHelpersRef.current.setValue(next)
        }
    }, [])

    const servicesMap = useMemo(() => {
        const map = new Map<number, ExtraServiceDetails>()
        for (const service of services) {
            map.set(service.id, service)
        }
        return map
    }, [services])

    const isAutoAdded = useCallback(
        (action: ExtraServiceDetails["action"]) => {
            if (action === ExtraServiceActionType.ADD_ALWAYS) return true
            if (
                action === ExtraServiceActionType.ADD_WHEN_PICK_UP &&
                deliveryType === DeliveryFulfillmentType.PICK_UP
            ) {
                return true
            }
            if (
                action === ExtraServiceActionType.ADD_WHEN_DELIVERED &&
                deliveryType === DeliveryFulfillmentType.DELIVERY
            ) {
                return true
            }
            return false
        },
        [deliveryType],
    )

    const hasAutoAddedServices = useMemo(
        () => services.some((s) => s.isMandatory || isAutoAdded(s.action)),
        [services, isAutoAdded],
    )

    // Expand the parent section when any auto-added services are present
    useEffect(() => {
        if (hasAutoAddedServices) {
            onAutoExpandRef.current?.()
        }
    }, [hasAutoAddedServices])

    // Runs when services or delivery type changes.
    // - Removes services no longer returned by the API (stale).
    // - Removes auto-added services that no longer match the current delivery type.
    // - Adds services that should be auto-added for the current delivery type.
    // setDeliveries has an isEqual guard so calling it without actual changes is a no-op.
    useEffect(() => {
        const currentIds = new Set(services.map((s) => s.id))
        const autoAddServices = services.filter((s) => {
            if (s.isMandatory) return true
            if (s.action === ExtraServiceActionType.ADD_ALWAYS) return true
            if (
                s.action === ExtraServiceActionType.ADD_WHEN_PICK_UP &&
                deliveryType === DeliveryFulfillmentType.PICK_UP
            ) {
                return true
            }
            if (
                s.action === ExtraServiceActionType.ADD_WHEN_DELIVERED &&
                deliveryType === DeliveryFulfillmentType.DELIVERY
            ) {
                return true
            }
            return false
        })

        // Determine which services are newly being added (not yet in cart) to show snackbars
        const currentDelivery = deliveriesRef.current?.deliveryTimes?.[deliveryIndex]
        const newlyAddedServices = currentDelivery
            ? autoAddServices.filter(
                  (service) =>
                      !currentDelivery.orderLines.some((l) => Number(l.id) === Number(service.id) && l.isExtraService),
              )
            : []

        setDeliveries((current) => {
            if (!Array.isArray(current?.deliveryTimes)) return current
            return {
                ...current,
                deliveryTimes: current.deliveryTimes.map((delivery, idx) => {
                    if (idx !== deliveryIndex) return delivery

                    // Remove stale services and auto-added services that no longer match delivery type
                    let orderLines = delivery.orderLines.filter((line) => {
                        if (!line.isExtraService) return true
                        if (!currentIds.has(Number(line.id))) return false
                        const pw = line as ProductWithQuantity
                        if (pw.isAutoAddedExtraService) {
                            return autoAddServices.some((s) => Number(s.id) === Number(line.id))
                        }
                        // Pre-populated services from edit mode lack isAutoAddedExtraService.
                        // Apply the same removal logic for any service that is auto-add designated.
                        const src = services.find((s) => Number(s.id) === Number(line.id))
                        if (
                            src &&
                            (src.isMandatory ||
                                src.action === ExtraServiceActionType.ADD_ALWAYS ||
                                src.action === ExtraServiceActionType.ADD_WHEN_PICK_UP ||
                                src.action === ExtraServiceActionType.ADD_WHEN_DELIVERED)
                        ) {
                            return autoAddServices.some((s) => Number(s.id) === Number(line.id))
                        }
                        return true
                    })

                    // Auto-add services that match the current delivery type
                    for (const service of autoAddServices) {
                        const isAdded = orderLines.some((l) => Number(l.id) === Number(service.id) && l.isExtraService)
                        if (!isAdded) {
                            orderLines = [
                                ...orderLines,
                                {
                                    id: service.id,
                                    name: service.name,
                                    price: calculateServicePrice(service, seatCountRef.current),
                                    isActive: service.isActive ?? true,
                                    quantity: 1,
                                    isExtraService: true,
                                    isAutoAddedExtraService: true,
                                    ...(service.isMeetingTimeRequired ? { isMeetingTimeRequired: true } : {}),
                                    ...(service.isMeetingLocationRequired ? { isMeetingLocationRequired: true } : {}),
                                    ...(service.pricingType ? { extraServicePricingType: service.pricingType } : {}),
                                    ...(service.pricePerSeat ? { extraServicePricePerSeat: service.pricePerSeat } : {}),
                                    ...(service.minimumPrice ? { extraServiceMinPrice: service.minimumPrice } : {}),
                                    ...(service.maximumPrice ? { extraServiceMaxPrice: service.maximumPrice } : {}),
                                },
                            ]
                        }
                    }

                    return { ...delivery, orderLines }
                }),
            }
        })

        // Show snackbars for newly auto-added services (suppress in credit note/refund mode)
        if (!isRefundOrder) {
            newlyAddedServices.forEach((service, serviceIndex) => {
                const price = calculateServicePrice(service, seatCountRef.current)
                const amount = moneyFactoryRef.current.format(toDinero(price))
                const message = l10nRef.current.getString("create-order-extra-services-auto-added-snackbar", {
                    amount,
                    name: service.name,
                })
                enqueueSnackbarRef.current(message, {
                    content: (key, msg) => (
                        <AutoAddedServiceSnackbar id={key} message={msg} isFirst={serviceIndex === 0} />
                    ),
                    persist: true,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                })
            })
        }
    }, [services, deliveryType, setDeliveries, deliveryIndex, isRefundOrder])

    useEffect(() => {
        if (!seatCount) return

        setDeliveries((current) => {
            if (!Array.isArray(current?.deliveryTimes)) return current
            return {
                ...current,
                deliveryTimes: current.deliveryTimes.map((delivery, idx) => {
                    if (idx !== deliveryIndex) return delivery
                    return {
                        ...delivery,
                        orderLines: delivery.orderLines.map((orderLine) => {
                            const service = typeof orderLine.id === "number" && servicesMap.get(orderLine.id)
                            if (!service || !orderLine.isExtraService) return orderLine

                            const updatedPrice = calculateServicePrice(service, seatCount)
                            return {
                                ...orderLine,
                                price: updatedPrice,
                                adjustedPrice: updatedPrice,
                            }
                        }),
                    }
                }),
            }
        })
    }, [seatCount, servicesMap, setDeliveries, deliveryIndex])

    const handleAddService = (service: ExtraServiceDetails) => {
        const totalPrice = calculateServicePrice(service, seatCount)

        const newOrderLine: ProductWithQuantity = {
            id: service.id,
            name: service.name,
            price: totalPrice,
            isActive: service.isActive ?? true,
            quantity: 1,
            isExtraService: true,
            ...(service.pricingType === "PER_SEAT" && service.pricePerSeat ? { itemPrice: service.pricePerSeat } : {}),
            ...(service.isMeetingTimeRequired ? { isMeetingTimeRequired: true } : {}),
            ...(service.isMeetingLocationRequired ? { isMeetingLocationRequired: true } : {}),
            ...(service.pricingType ? { extraServicePricingType: service.pricingType } : {}),
            ...(service.pricePerSeat ? { extraServicePricePerSeat: service.pricePerSeat } : {}),
            ...(service.minimumPrice ? { extraServiceMinPrice: service.minimumPrice } : {}),
            ...(service.maximumPrice ? { extraServiceMaxPrice: service.maximumPrice } : {}),
        }

        const updatedDeliveries = deliveries.deliveryTimes.map((delivery, idx) => {
            if (idx !== deliveryIndex) return delivery
            if (isServiceAdded(delivery, service.id)) return delivery

            return {
                ...delivery,
                orderLines: [...delivery.orderLines, newOrderLine],
            }
        })

        deliveriesHelpers.setValue({
            ...deliveries,
            deliveryTimes: updatedDeliveries,
        })
    }

    const handleRemoveService = (serviceId: number) => {
        const updatedDeliveries = deliveries.deliveryTimes.map((delivery, idx) => {
            if (idx !== deliveryIndex) return delivery

            return {
                ...delivery,
                orderLines: delivery.orderLines.filter(
                    (p) => !(Number(p.id) === Number(serviceId) && p.isExtraService),
                ),
            }
        })

        deliveriesHelpers.setValue({
            ...deliveries,
            deliveryTimes: updatedDeliveries,
        })
    }

    const delivery = deliveries.deliveryTimes[deliveryIndex]
    if (!delivery) return null

    // Only optional services are shown — mandatory and auto-added ones are handled silently
    const displayServices = services.filter((s) => !s.isMandatory && !isAutoAdded(s.action))

    if (displayServices.length === 0) return null

    return (
        <Stack gap={2} className={classes.wrap}>
            <LocalizedStrict
                id="create-order-extra-services-delivery"
                attrs={{ title: true }}
                vars={{ time: deliveryTime }}
            >
                <ExtraServicesAccordion title={`Delivery at ${deliveryTime}`} defaultExpanded={hasAutoAddedServices}>
                    {displayServices.map((service, serviceIndex) => {
                        const { id, name, description, pricingType, action, isMandatory } = service
                        const total = calculateServicePrice(service, seatCount)
                        const isAlreadyAdded = isServiceAdded(delivery, id)
                        const isPerSeat = pricingType === "PER_SEAT"
                        const isActionAutoAdded = !isMandatory && isAutoAdded(action)

                        return (
                            <Stack key={id}>
                                {serviceIndex > 0 && <Divider className={classes.extraServicesDivider} />}
                                <Stack gap=".75rem">
                                    <Stack gap=".25rem">
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            gap="1rem"
                                        >
                                            <Stack flexDirection="row" gap=".5rem" alignItems="center">
                                                <Typography fontWeight="bold">{name}</Typography>
                                                {description && (
                                                    <Tooltip title={description}>
                                                        <FontAwesomeIcon icon={faCircleInfo} className={classes.icon} />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                            <Typography variant="subtitle1">
                                                {moneyFactory.format(toDinero(total))}
                                            </Typography>
                                        </Stack>

                                        {isPerSeat && service.minimumPrice && service.maximumPrice && (
                                            <LocalizedStrict
                                                id="create-order-extra-services-min-max"
                                                vars={{
                                                    min: moneyFactory.format(toDinero(service.minimumPrice)),
                                                    max: moneyFactory.format(toDinero(service.maximumPrice)),
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    Min. {moneyFactory.format(toDinero(service.minimumPrice))} - Max.
                                                    {moneyFactory.format(toDinero(service.maximumPrice))}
                                                </Typography>
                                            </LocalizedStrict>
                                        )}
                                    </Stack>

                                    <Stack flexDirection="row" gap="1rem" justifyContent="flex-end" alignItems="center">
                                        {isPerSeat && service.pricePerSeat && (
                                            <LocalizedStrict
                                                id="create-order-extra-services-seats-breakdown"
                                                vars={{
                                                    count: seatCount,
                                                    price: moneyFactory.format(toDinero(service.pricePerSeat)),
                                                }}
                                            >
                                                <Typography variant="subtitle2">
                                                    {seatCount} seats x{" "}
                                                    {moneyFactory.format(toDinero(service.pricePerSeat))}
                                                </Typography>
                                            </LocalizedStrict>
                                        )}
                                        {isMandatory ? (
                                            <LocalizedStrict
                                                id="create-order-extra-services-mandatory-tooltip"
                                                attrs={{ title: true }}
                                            >
                                                <Tooltip title="">
                                                    <span>
                                                        <LocalizedStrict id="create-order-extra-services-remove-button">
                                                            <StandardButton
                                                                size="small"
                                                                fullWidth={false}
                                                                variant="outlined"
                                                                color="error"
                                                                disabled
                                                            >
                                                                Remove
                                                            </StandardButton>
                                                        </LocalizedStrict>
                                                    </span>
                                                </Tooltip>
                                            </LocalizedStrict>
                                        ) : isActionAutoAdded ? (
                                            <LocalizedStrict id="create-order-extra-services-always-added-label">
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Included
                                                </Typography>
                                            </LocalizedStrict>
                                        ) : isAlreadyAdded ? (
                                            <LocalizedStrict id="create-order-extra-services-remove-button">
                                                <StandardButton
                                                    size="small"
                                                    fullWidth={false}
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => handleRemoveService(id)}
                                                    disabled={isRefundOrder}
                                                >
                                                    Remove
                                                </StandardButton>
                                            </LocalizedStrict>
                                        ) : (
                                            <LocalizedStrict id="create-order-extra-services-add-button">
                                                <StandardButton
                                                    size="small"
                                                    fullWidth={false}
                                                    onClick={() => handleAddService(service)}
                                                    disabled={isRefundOrder}
                                                >
                                                    Add
                                                </StandardButton>
                                            </LocalizedStrict>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack>
                        )
                    })}
                </ExtraServicesAccordion>
            </LocalizedStrict>
        </Stack>
    )
}
